'use client';

import type { ReactNode } from 'react';
import type { TagData } from 'src/types/tag-data';
import { useLayoutEffect, useRef, useState } from 'react';
import { Badge } from '@savvy-kit/components/badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@savvy-kit/components/dropdown-menu';
import { cn } from '@savvy-kit/lib/utils';
import Tag from '@savvy-kit/patterns/tag';
import { isNil } from 'es-toolkit';
import { tagIndicatorPropsFromTagData } from 'src/utils/advisor-dash/tag-indicator-from-tag-data';

import { CategoryIndicator } from '../tags/category-indicator';
import { visibleWrappedTagCount } from '../tags/visible-wrapped-tag-count';

const MAX_VISIBLE_TAGS = 3;
// Reserved before the "+N" badge exists, so the fit never depends on whether it is currently
// rendered. Sized for "+99" — over-reserving costs at most one chip.
const OVERFLOW_BADGE_WIDTH_PX = 44;

type HouseholdTag = {
  readonly id: string;
  readonly organizationHouseholdTag: {
    readonly name: string;
    readonly isSmart?: boolean | null;
    readonly organizationHouseholdCategory: {
      readonly hexColorCode: string | null;
      readonly emoji?: string | null;
    } | null;
  } | null;
} | null;

/**
 * Extracts valid TagData from householdTags array
 */
export function extractTagsFromHouseholdTags(
  householdTags: readonly HouseholdTag[] | null | undefined,
): readonly TagData[] {
  if (!householdTags || householdTags.length === 0) {
    return [];
  }

  return householdTags
    .map((householdTag): TagData | null => {
      const orgTag = householdTag?.organizationHouseholdTag;
      if (!orgTag) return null;

      const category = orgTag.organizationHouseholdCategory;
      const hexColorCode = typeof category?.hexColorCode === 'string' ? category.hexColorCode : null;
      const emoji = typeof category?.emoji === 'string' ? category.emoji : null;
      const isSmart = orgTag.isSmart === true;

      return {
        id: householdTag.id,
        name: orgTag.name,
        hexColorCode,
        emoji,
        isSmart,
      };
    })
    .filter((tag): tag is TagData => !isNil(tag));
}

type Props = {
  readonly tags: readonly TagData[];
  readonly emptyState?: ReactNode;
  readonly containerClassName?: string;
  readonly tagWrapperClassName?: string;
  readonly dropdownMaxWidth?: string;
  /** className applied to each visible chip (e.g. to match a compact call site). */
  readonly tagClassName?: string;
  /** Suppress the category indicator dot on chips and in the overflow popover. */
  readonly hideIndicator?: boolean;
  /** className applied to each tag label in the overflow popover. */
  readonly popoverItemClassName?: string;
  /**
   * Clamp the chips to this many wrapped lines instead of the default count cap, measuring what
   * actually fits at the current width. Use in table cells, where a tall tag stack drags the whole
   * row's height with it.
   */
  readonly maxLines?: number;
};

/**
 * How many chips fit in `maxLines`, remeasured as the column resizes. Returns `tags.length` until
 * the measuring pass has run, so the caller renders every chip once and their natural widths can be
 * cached — reading widths back off an already-clipped row reports zero for the dropped chips and
 * makes the count unrecoverable.
 */
function useWrappedVisibleCount(tags: readonly TagData[], maxLines: number | undefined) {
  const chipWidthsRef = useRef<readonly number[]>([]);
  const gapRef = useRef(0);

  // A callback ref rather than `useRef`, because the container is absent on the empty-tags render.
  // Tracking the node as state is what re-runs the effects below once it finally mounts.
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [measuring, setMeasuring] = useState(true);

  // Every field that changes a chip's rendered width, not just its identity: the indicator is a
  // leading element whose presence and glyph both move the measurement.
  const tagsKey = tags
    .map((tag) => `${tag.id}:${tag.name}:${tag.emoji ?? ''}:${tag.hexColorCode ?? ''}:${tag.isSmart === true}`)
    .join('|');

  useLayoutEffect(() => {
    if (isNil(maxLines)) return;
    setMeasuring(true);
  }, [tagsKey, maxLines]);

  useLayoutEffect(() => {
    if (isNil(maxLines) || !measuring || !container) return;
    chipWidthsRef.current = Array.from(container.children, (chip) => chip.getBoundingClientRect().width);
    // The gap lives in a caller-supplied className, so it has to be read rather than assumed.
    gapRef.current = Number.parseFloat(getComputedStyle(container).columnGap) || 0;
    setMeasuring(false);
  }, [container, maxLines, measuring]);

  useLayoutEffect(() => {
    if (isNil(maxLines) || !container) return;
    // Seeded before observing: the observer's first callback lands after paint, which would
    // otherwise show a frame of "+N" with every chip dropped.
    setContainerWidth(container.clientWidth);
    const observer = new ResizeObserver(([entry]) => {
      if (entry) setContainerWidth(entry.contentRect.width);
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, [container, maxLines]);

  // No ref in the unclamped mode: attaching one would schedule a `setContainer` render per cell on
  // mount, and these tables render a cell per row for a node nothing here reads.
  if (isNil(maxLines)) {
    return { containerRef: undefined, visibleCount: Math.min(tags.length, MAX_VISIBLE_TAGS), clamped: false };
  }

  const visibleCount = measuring
    ? tags.length
    : visibleWrappedTagCount({
        chipWidths: chipWidthsRef.current,
        gapPx: gapRef.current,
        availableWidth: containerWidth,
        overflowWidth: OVERFLOW_BADGE_WIDTH_PX,
        maxLines,
      });

  return { containerRef: setContainer, visibleCount, clamped: true };
}

export function HouseholdTagsDisplay({
  tags,
  emptyState,
  containerClassName = 'flex flex-wrap gap-2',
  tagWrapperClassName,
  dropdownMaxWidth = '200px',
  tagClassName,
  hideIndicator = false,
  popoverItemClassName,
  maxLines,
}: Props) {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { containerRef, visibleCount, clamped } = useWrappedVisibleCount(tags, maxLines);

  const handleMouseEnter = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setIsPopoverOpen(true);
  };

  const handleMouseLeave = () => {
    closeTimer.current = setTimeout(() => setIsPopoverOpen(false), 100);
  };

  if (tags.length === 0) {
    if (!isNil(emptyState)) {
      return emptyState;
    }
    return (
      <div className="flex-1 pl-3">
        <span className="text-sm text-secondary">-</span>
      </div>
    );
  }

  const remainingTags = tags.slice(visibleCount);
  const remainingCount = remainingTags.length;
  // Clamped cells keep every chip mounted and hide the overflow, so the measuring pass can read
  // natural widths back off chips this component has already dropped.
  const renderedTags = clamped ? tags : tags.slice(0, visibleCount);

  return (
    <div ref={containerRef} className={containerClassName}>
      {renderedTags.map((tag, index) => {
        const isHidden = clamped && index >= visibleCount;
        const tagElement = (
          <Tag
            key={tag.id}
            {...(hideIndicator ? {} : tagIndicatorPropsFromTagData(tag))}
            type="read-only"
            className={cn(tagClassName, isHidden && 'hidden')}
            labelClassName="max-w-[200px] truncate"
          >
            {tag.name}
          </Tag>
        );

        if (!isNil(tagWrapperClassName)) {
          return (
            <div key={tag.id} className={cn(tagWrapperClassName, isHidden && 'hidden')}>
              {tagElement}
            </div>
          );
        }

        return tagElement;
      })}
      {remainingCount > 0 && (
        <DropdownMenu open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
          <DropdownMenuTrigger
            className="data-popup-open:ring-0 data-popup-open:border-transparent"
            render={
              <button
                type="button"
                className="cursor-pointer border-none bg-transparent p-0 hover:opacity-70 shrink-0 focus:outline-none focus:ring-0"
                aria-label={`Show ${remainingCount} more tags`}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
              />
            }
          >
            <Badge variant="outline" className="shrink-0 border-0">
              +{remainingCount}
            </Badge>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="p-2 overflow-x-hidden max-h-[40vh] overflow-y-auto outline-none"
            align="start"
            sideOffset={8}
            style={{ maxWidth: dropdownMaxWidth }}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            {remainingTags.map((tag) => (
              <div key={tag.id} className="px-2 py-1.5 flex items-center gap-2 min-w-0">
                {!hideIndicator && (
                  <CategoryIndicator hexColorCode={tag.hexColorCode} emoji={tag.emoji} smart={tag.isSmart === true} />
                )}
                <span className={cn('truncate min-w-0 text-sm text-dark', popoverItemClassName)}>{tag.name}</span>
              </div>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}
